/**
 * Environment Detection Utility
 * Detects whether the add-in is running in WOPI, SharePoint, or Desktop environment
 */

export const EnvironmentType = {
  WOPI: 'WOPI',
  SHAREPOINT: 'SHAREPOINT', 
  DESKTOP: 'DESKTOP',
  BROWSER: 'BROWSER'
};

/**
 * Detect the current Office environment
 * @param {Object} officeInfo - Office.onReady info object
 * @returns {Object} - Environment detection result
 */
export const detectEnvironment = (officeInfo) => {
  console.log("[ENV-DETECTOR] Starting environment detection...");
  
  const platform = Office.context.platform;
  const host = officeInfo?.host;
  const userAgent = navigator.userAgent;
  const hostname = window.location.hostname;
  
  console.log("[ENV-DETECTOR] Environment details:");
  console.log("[ENV-DETECTOR] - Platform:", platform);
  console.log("[ENV-DETECTOR] - Host:", host);
  console.log("[ENV-DETECTOR] - Hostname:", hostname);
  console.log("[ENV-DETECTOR] - User Agent:", userAgent);
  
  // First check if we're actually running inside Office
  // If host is null or undefined, we're likely in a browser, not Office
  if (!host || host === null) {
    console.log("[ENV-DETECTOR] ✅ Browser detected (not running inside Office)");
    return {
      type: EnvironmentType.BROWSER,
      platform,
      hostname,
      isOfficeOnline: false,
      documentUrl: null
    };
  }
  
  // Check if it's Office Online (OfficeOnline platform)
  const isOfficeOnline = platform === Office.PlatformType.OfficeOnline;
  
  if (!isOfficeOnline) {
    // Desktop Word (when actually running inside Office)
    console.log("[ENV-DETECTOR] ✅ Desktop Word detected");
    return {
      type: EnvironmentType.DESKTOP,
      platform,
      hostname,
      isOfficeOnline: false,
      documentUrl: null
    };
  }
  
  // For Office Online, distinguish between WOPI and SharePoint using document.url
  try {
    const docUrl = Office.context.document.url;
    console.log("[ENV-DETECTOR] Office.context.document.url:", docUrl || "empty");
    
    if (!docUrl || docUrl.trim() === "") {
      // Empty document URL = WOPI / True Office Online
      console.log("[ENV-DETECTOR] ✅ WOPI detected - document.url is empty");
      return {
        type: EnvironmentType.WOPI,
        platform,
        hostname,
        isOfficeOnline: true,
        documentUrl: docUrl
      };
    } else {
      // Has document URL = SharePoint/OneDrive
      console.log("[ENV-DETECTOR] ✅ SharePoint detected - document.url has value");
      return {
        type: EnvironmentType.SHAREPOINT,
        platform,
        hostname,
        isOfficeOnline: true,
        documentUrl: docUrl
      };
    }
  } catch (error) {
    console.error("[ENV-DETECTOR] Office.context.document.url access failed:", error.message);
    
    // Fallback to hostname-based detection
    const isSharePointHostname = hostname.includes('sharepoint.com') || 
                                hostname.includes('.sharepoint.') || 
                                userAgent.includes('SharePoint');
    
    if (isSharePointHostname) {
      console.log("[ENV-DETECTOR] ✅ SharePoint detected via fallback hostname detection");
      return {
        type: EnvironmentType.SHAREPOINT,
        platform,
        hostname,
        isOfficeOnline: true,
        documentUrl: null,
        detectionMethod: 'fallback'
      };
    } else {
      console.log("[ENV-DETECTOR] ✅ WOPI detected via fallback");
      return {
        type: EnvironmentType.WOPI,
        platform,
        hostname,
        isOfficeOnline: true,
        documentUrl: null,
        detectionMethod: 'fallback'
      };
    }
  }
};

/**
 * Development mode override for testing
 * @param {string} forceMode - 'wopi', 'sharepoint', 'desktop', or 'auto'
 * @param {Object} detectedEnv - The normally detected environment
 * @returns {Object} - Final environment result
 */
export const applyDevelopmentOverride = (forceMode, detectedEnv) => {
  const isDevelopment = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';
  
  if (!isDevelopment || forceMode === 'auto') {
    return detectedEnv;
  }
  
  console.log("[ENV-DETECTOR] 🔧 Development mode override:", forceMode);
  
  const overrideMap = {
    'wopi': EnvironmentType.WOPI,
    'sharepoint': EnvironmentType.SHAREPOINT,
    'desktop': EnvironmentType.DESKTOP
  };
  
  return {
    ...detectedEnv,
    type: overrideMap[forceMode] || detectedEnv.type,
    isDevelopmentOverride: true
  };
};
